import { useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import SessionActions from '../../account/SessionActions';
import useAppSelector from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import type {
  CustomDomainsSettingsQuery,
  CustomDomainsSettingsQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import { PlanTier } from '../../graphql/generated/graphqlTypes';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import UIActions from '../../ui/UIActions';
import handleApolloError from '../../utils/error/handleApolloError';
import styles from './AdminTeamView.module.scss';
import AllowedIframeHostsSettings from './AllowedIframeHostsSettings';
import CustomDomainSettings, {
  CUSTOM_DOMAINS_QUERY,
} from './CustomDomainSettings';
import DeleteTeam from './DeleteTeam';
import InvitationSettings from './InvitationSettings';
import PublicContentSettings from './PublicContentSettings';
import TeamIconEditor from './TeamIconEditor';
import TopLevelTopicSettings from './TopLevelTopicSettings';
import UserGroupSettings from './UserGroupsSettings';
import redirectToHost from './redirectToHost';
import splitSubdomain from './splitSubdomain';
import useUpdateOrganization from './useUpdateOrganization';

const useDefaultHostRedirect = ({
  enabled,
  effectiveHost,
  host,
  customDomains,
}: {
  enabled: boolean;
  customDomains: { hostname: string }[];
  effectiveHost: string;
  host: string;
}) => {
  const dispatch = useDispatch();
  const prevEffectiveHostRef = useRef(effectiveHost);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    const effectiveHostChanged = effectiveHost !== prevEffectiveHostRef.current;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    const currentHostRemoved = ![
      ...customDomains.map(({ hostname }) => hostname),
      host,
    ].includes(window.location.hostname);
    if (
      (effectiveHostChanged && window.location.hostname !== effectiveHost) ||
      currentHostRemoved
    ) {
      redirectToHost(effectiveHost, () => {
        if (currentHostRemoved) {
          dispatch(SessionActions.clearSession());
        }
      });
    }
  }, [enabled, dispatch, host, effectiveHost, customDomains]);

  useEffect(() => {
    prevEffectiveHostRef.current = effectiveHost;
  }, [enabled, effectiveHost]);
};

interface AdminTeamViewContentProps {
  name: string;
  host: string;
  effectiveHost: string;
  isBusinessOrEnterprise: boolean;
  disableDefaultHostRedirect: boolean;
}

export const AdminTeamViewContent = ({
  name,
  host,
  effectiveHost,
  isBusinessOrEnterprise,
  disableDefaultHostRedirect,
}: AdminTeamViewContentProps) => {
  const dispatch = useDispatch();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
  const updateOrganization = useUpdateOrganization({ host });
  const handleNameChange = (values: { name: string }) =>
    updateOrganization({ name: values.name }).then(() => {
      setIsEditingName(false);
    });

  const [subdomain, baseDomain] = splitSubdomain(host);

  const handleSubdomainChange = (values: { subdomain?: string }) => {
    if (values.subdomain === subdomain) {
      setIsEditingSubdomain(false);
      return;
    }

    let isResolved = false;
    dispatch(
      UIActions.confirmModal({
        title: 'Change default domain?',
        message: `Any links to your previous domain ${host} will no longer work.`,
        confirmButtonText: 'Change',
        close: () => {
          dispatch(UIActions.closeModal());
          if (!isResolved) {
            setIsEditingSubdomain(false);
          }
        },
        onConfirm: async () => {
          isResolved = true;
          try {
            await updateOrganization({ subdomain: values.subdomain });
            setIsEditingSubdomain(false);
          } catch (error) {
            handleApolloError(error, {
              VALIDATION: (graphqlError: { message?: string }) => {
                const message = graphqlError?.message?.replace(
                  /^VALIDATION: \[subdomain\]/,
                  'Subdomain',
                );
                if (message) {
                  dispatch(
                    UIActions.pushToast({ level: 'error', message }, 3000),
                  );
                }
              },
            });
          }
        },
      }),
    );
  };

  const customDomainsResponse = useQuery<
    CustomDomainsSettingsQuery,
    CustomDomainsSettingsQueryVariables
  >(CUSTOM_DOMAINS_QUERY, { fetchPolicy: 'network-only' });

  const customDomains =
    customDomainsResponse.data?.session?.organization?.customDomains ?? [];

  useDefaultHostRedirect({
    enabled: !customDomainsResponse.loading && !disableDefaultHostRedirect,
    customDomains,
    host,
    effectiveHost,
  });

  return (
    <>
      <SettingsSection>
        <Cards>
          <Card>
            <TeamIconEditor />
          </Card>
          {isEditingName ? (
            <SettingCard
              description={
                <Form<{ name: string }>
                  initialValues={{ name }}
                  onSubmit={handleNameChange}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      className={styles.field}
                      id="organizationName"
                      margin="none"
                      name="name"
                      required
                    />
                    <div>
                      <Submit variant="primary">Save</Submit>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="organizationName">Team name</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingName(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Team name"
              description={name}
            />
          )}
          {isEditingSubdomain ? (
            <SettingCard
              description={
                <Form<{ subdomain?: string }>
                  initialValues={{ subdomain }}
                  onSubmit={handleSubdomainChange}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      append={baseDomain}
                      className={styles.field}
                      id="subdomain"
                      margin="none"
                      name="subdomain"
                      required
                    />
                    <div>
                      <Submit variant="primary">Save</Submit>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="subdomain">Subdomain</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingSubdomain(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Subdomain"
              description={
                <>
                  {host}
                  {isBusinessOrEnterprise &&
                    customDomains.length > 0 &&
                    host === effectiveHost && (
                      <span className={styles.badgeContainer}>
                        <Badge color="yellow">Default</Badge>
                      </span>
                    )}
                </>
              }
            />
          )}
        </Cards>
      </SettingsSection>
      <SettingsSection header="Permissions">
        <Cards>
          <InvitationSettings />
          <UserGroupSettings />
          <PublicContentSettings />
          <TopLevelTopicSettings />
          <AllowedIframeHostsSettings />
        </Cards>
      </SettingsSection>
      <CustomDomainSettings
        customDomains={customDomains}
        loading={customDomainsResponse.loading}
        host={host}
        effectiveHost={effectiveHost}
        isBusinessOrEnterprise={isBusinessOrEnterprise}
      />
      <SettingsSection header="Danger zone">
        <Cards>
          <DeleteTeam />
        </Cards>
      </SettingsSection>
    </>
  );
};

const AdminTeamView = () => {
  const name = useAppSelector((state) => state.session.organization.name);
  const host = useAppSelector((state) => state.session.organization.host);
  const effectiveHost = useAppSelector(
    (state) => state.session.organization.effectiveHost,
  );
  const isBusinessOrEnterprise = useAppSelector((state) =>
    state.session.organization.planTier
      ? [PlanTier.Business, PlanTier.Enterprise].includes(
          state.session.organization.planTier,
        )
      : false,
  );
  return (
    <SettingsContainerView title="Team">
      <AdminTeamViewContent
        name={name}
        host={host}
        effectiveHost={effectiveHost}
        isBusinessOrEnterprise={isBusinessOrEnterprise}
        disableDefaultHostRedirect={false}
      />
    </SettingsContainerView>
  );
};

export default AdminTeamView;
